import { useLoaderData } from '@remix-run/react'

import { forwardRef, useContext, useEffect, useState } from 'react'
import type { CallbackArgument, Focused } from 'react-credit-cards-2'
import Cards from 'react-credit-cards-2'
import { Button } from '../../primitives'
import CardNumber from './CardNumber'
import CpfNumberInput from './Cpf'
import CvvInput from './Cvv'
import DueDateInput from './DueDate'
import HolderNameInput from './HolderName'
import InstallmentsSelect from './InstallmentsSelect'
import { CreditCardContext } from './Provider'
import { Undo } from '~/icons'
import type { loader } from '~/routes/checkout.payment'
import 'react-credit-cards-2/dist/es/styles-compiled.css'

type Props = {
  backToCardsList: () => void
}
const NewCreditCardForm = forwardRef((props: Props, ref) => {
  const {
    setAccountId,
    setValidationCode,
    validationCode,
    setHolderName,
    setDueDate,
    setCpf,
    setInstallmentsSelected,
    setHasBeenAddedNewCard,
    setCardNumber,
    cardNumber,
    setCreditCard,
    creditCard,
  } = useContext(CreditCardContext)

  const { paymentData } = useLoaderData<typeof loader>()

  const { backToCardsList } = props
  const [focus, setFocus] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: '',
    focus: '',
  })

  const [error, setError] = useState('')

  useEffect(() => {
    setHasBeenAddedNewCard(true)
    resetVariables()
    return () => setHasBeenAddedNewCard(false)
  }, [])

  const resetVariables = () => {
    setAccountId('')
    setValidationCode('')
    setHolderName('')
    setDueDate({ month: '', year: '', isValid: false })
    setCpf('')
    setCardNumber('')
    setInstallmentsSelected({ count: 0, value: 0 })
    setCreditCard(null)
  }

  const credidcardCallback = (type: CallbackArgument, isValid: boolean) => {
    setError('')

    const issuerMap: Record<string, string> = {
      'american-express': 'American Express',
    }

    const normalizedIssuer = issuerMap[type.issuer.toLocaleLowerCase()] ?? type.issuer

    const payment = paymentData.paymentSystems.find(
      (payment) => payment.name.toLocaleLowerCase() === normalizedIssuer.toLocaleLowerCase(),
    )

    if (payment === undefined && cardNumber.length > 5) {
      setError('Cartão inválido. Por favor, cadastre outro cartão ou verifique se foi digitado corretamente.')
      setCreditCard(null)
      return
    }

    setCreditCard(payment ?? null)
  }

  const installmentsOptions = paymentData.installmentOptions.find(
    (installment) => installment.paymentSystem === creditCard?.id,
  )

  const formattedName = focus.name.length > 12 ? focus.name.slice(0, 12) + '...' : focus.name

  return (
    <div className="card-form-container flex flex-wrap md:py-5 flex-col lg:flex-row gap-2 justify-items-stretch bg-gray-10">
      <div className="form-wrapper order-2 lg:order-1 mt-3 lg:mt-0 max-w-[28.75rem]">
        <div className="flex items-center justify-between flex-col gap-y-5 px-5">
          <div className="flex w-full gap-5 flex-col lg:flex-row">
            <div className="flex flex-col gap-1 w-full max-w-full lg:max-w-[249px]">
              <HolderNameInput
                onFocus={({ target: { name, value } }) =>
                  setFocus((prev) => ({ ...prev, [name]: value, focus: 'name' }))
                }
                onNameChange={(username) => {
                  setHolderName(username)
                  setFocus((prev) => ({ ...prev, name: username }))
                }}
              />
            </div>

            <div className="flex flex-col gap-1 w-full max-w-full lg:max-w-[249px]">
              <CardNumber
                creditCard={creditCard!}
                onCreditCardNumberChange={(number) => setCardNumber(number)}
                onFocus={({ target: { name, value } }) =>
                  setFocus((prev) => ({ ...prev, [name]: value, focus: 'number' }))
                }
              />
            </div>
          </div>

          <div className="flex w-full gap-5 flex-col lg:flex-row">
            <div className="w-full gap-5 flex justify-between flex-row">
              <div className="flex flex-col gap-1 w-1/2">
                <DueDateInput
                  monthInputProps={{
                    onFocus: ({ target: { name, value } }) => {
                      setFocus((prev) => ({ ...prev, [name]: value, focus: 'expiry' }))
                    },
                  }}
                  yearInputProps={{
                    onFocus: ({ target: { name, value } }) => {
                      setFocus((prev) => ({ ...prev, [name]: value, focus: 'expiry' }))
                    },
                  }}
                  onDueDateChange={({ year, month, isValid }) => setDueDate({ year, month, isValid })}
                />
              </div>
              <div className="flex flex-col gap-1 w-1/2">
                <div className="flex flex-col gap-1">
                  <CvvInput
                    creditCard={creditCard!}
                    id="cvc"
                    name="cvc"
                    type="tel"
                    onCvvChange={(cvv) => setValidationCode(cvv)}
                    onFocus={() => setFocus((prev) => ({ ...prev, focus: 'cvc' }))}
                  />
                </div>
              </div>
            </div>

            <div className="w-full max-w-full lg:max-w-[138px] flex flex-col lg:flex-row">
              <div className="flex flex-col    w-full gap-1">
                <div className="flex flex-col gap-1">
                  <CpfNumberInput onCpfChange={(cpf) => setCpf(cpf)} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col w-full  mr-auto lg:max-w-[261px] lg:-mt-5 lg:mb-5">
            <InstallmentsSelect
              installments={installmentsOptions?.installments}
              setInstallmentsSelected={setInstallmentsSelected}
            />
          </div>
        </div>
      </div>

      <div className="card-container order-1 lg:order-2 lg:ml-5  lg:mr-auto">
        <Cards
          callback={credidcardCallback}
          cvc={validationCode}
          expiry={focus.expiry}
          focused={focus.focus as Focused}
          name={formattedName}
          number={cardNumber}
        />

        {error && <p className="text-red-400 mt-2 text-sm max-w-64 mx-auto block">{error}</p>}
      </div>

      <Button
        className="flex order-3 items-center lg:mt-2 mt-4 p-0 w-fit mx-5"
        onPress={() => {
          resetVariables()
          backToCardsList()
        }}
      >
        <Undo className="fill-primary-orange h-4 w-4" />
        <p className="ml-2 text-sm text-primary-orange underline">Voltar para lista de cartões</p>
      </Button>
    </div>
  )
})

NewCreditCardForm.displayName = 'NewCreditCardForm'

export default NewCreditCardForm
